import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiShield, FiLock, FiUnlock, FiKey, FiEye, FiAlertOctagon, 
  FiClock, FiSlash, FiPause, FiServer, FiUsers, FiCpu, FiCheckCircle, FiInfo
} = FiIcons;
import { mockSecurityLogs } from '../data/mockData';

const SecurityCentre = () => {
  const [vaultLocked, setVaultLocked] = useState(true);
  const [showConfirm, setShowConfirm] = useState(null);

  const emergencyActions = [
    { id: 'pause_all', label: 'Pause All Sending', icon: FiPause, color: 'bg-red-600', hover: 'hover:bg-red-700' },
    { id: 'pause_provider', label: 'Pause One Provider', icon: FiServer, color: 'bg-slate-900', hover: 'hover:bg-slate-800' },
    { id: 'pause_client', label: 'Pause One Client', icon: FiUsers, color: 'bg-slate-900', hover: 'hover:bg-slate-800' },
    { id: 'disable_key', label: 'Disable API Key', icon: FiSlash, color: 'bg-slate-900', hover: 'hover:bg-slate-800' },
    { id: 'maintenance', label: 'Maintenance Mode', icon: FiAlertOctagon, color: 'bg-amber-600', hover: 'hover:bg-amber-700' },
    { id: 'disable_ai', label: 'Disable AI Agents', icon: FiCpu, color: 'bg-slate-900', hover: 'hover:bg-slate-800' },
  ];

  return (
    <div className="space-y-6 animate-in slide-in-from-left-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Internal Security Centre</h2>
          <p className="text-sm text-slate-500">BPA Administrative access, vault controls and emergency overrides.</p>
        </div>
        <div className="px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl text-xs font-bold flex items-center gap-2">
          <SafeIcon icon={FiCheckCircle} /> Staff Access Verified: Admin (AD)
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vault Status */}
        <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
          <div className="relative z-10 space-y-6">
            <div className="flex justify-between items-start">
              <div className={`p-3 rounded-xl ${vaultLocked ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                <SafeIcon icon={vaultLocked ? FiLock : FiUnlock} className="text-2xl" />
              </div>
              <button 
                onClick={() => setVaultLocked(!vaultLocked)}
                className="px-4 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold transition-colors"
              >
                {vaultLocked ? 'Unlock Vault' : 'Lock Now'}
              </button>
            </div>
            <div>
              <h3 className="text-xl font-bold">Secure Vault: {vaultLocked ? 'Locked' : 'Active'}</h3>
              <p className="text-slate-400 text-xs mt-1">Auto-locks in 04:52 • PIN Required for reveals</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Reveals Today</p>
                <p className="text-lg font-bold">12</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Active Keys</p>
                <p className="text-lg font-bold">45</p>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-12 -right-12 opacity-10">
             <SafeIcon icon={FiShield} className="text-[200px]" />
          </div>
        </div>

        {/* Emergency Controls */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiAlertOctagon} className="text-red-500" />
              <h3 className="font-bold text-slate-800">Emergency Global Controls</h3>
            </div>
            <span className="text-[10px] font-bold text-red-600 animate-pulse">SYSTEM-WIDE IMPACT</span>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 flex-1">
            {emergencyActions.map(action => (
              <button 
                key={action.id}
                onClick={() => setShowConfirm(action.label)}
                className={`${action.color} ${action.hover} text-white p-4 rounded-xl transition-all shadow-sm flex flex-col items-center justify-center gap-2 group`}
              >
                <SafeIcon icon={action.icon} className="text-xl transition-transform group-hover:scale-110" />
                <span className="text-[11px] font-bold">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Security Audit Log */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-bold text-slate-800">Security & Access Audit</h3>
          </div>
          <div className="p-4 space-y-4">
            {mockSecurityLogs.map(log => (
              <div key={log.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded ${log.action.includes('Revealed') ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>
                    <SafeIcon icon={log.action.includes('Revealed') ? FiEye : FiKey} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-800">{log.action}: {log.target}</p>
                    <p className="text-[10px] text-slate-500">{log.user}</p>
                  </div>
                </div>
                <div className="text-right">
                   <p className="text-[10px] font-bold text-slate-400">{log.time}</p>
                   <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${log.status === 'Success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {log.status}
                   </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Access Roles */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-bold text-slate-800">Staff Access Matrix</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl border-l-4 border-blue-600">
                <h4 className="text-xs font-bold text-slate-800">BPA Owner</h4>
                <p className="text-[10px] text-slate-500 mt-1">Full system control, billing & security overrides.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border-l-4 border-blue-400">
                <h4 className="text-xs font-bold text-slate-800">BPA Admin</h4>
                <p className="text-[10px] text-slate-500 mt-1">Manage clients, apps and providers. No vault reveal.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border-l-4 border-slate-400">
                <h4 className="text-xs font-bold text-slate-800">BPA Operator</h4>
                <p className="text-[10px] text-slate-500 mt-1">Operational monitoring and log viewing only.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border-l-4 border-slate-200">
                <h4 className="text-xs font-bold text-slate-800">BPA Read-only</h4>
                <p className="text-[10px] text-slate-500 mt-1">Audit compliance view. No changes allowed.</p>
              </div>
            </div>
            <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-4">
               <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5" />
               <p className="text-xs text-blue-800 leading-relaxed">
                 Access is limited to <b>BPA Internal staff</b>. No client or tenant accounts exist in this gateway. Permission matrix is enforced at the identity layer.
               </p>
            </div>
          </div>
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 text-center space-y-6">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
              <SafeIcon icon={FiAlertOctagon} className="text-3xl" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">Confirm Global Action?</h3>
              <p className="text-sm text-slate-500 mt-2">You are about to: <b>{showConfirm}</b>. This will impact all email delivery routes immediately.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirm(null)} className="flex-1 py-2.5 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200">Cancel</button>
              <button onClick={() => setShowConfirm(null)} className="flex-1 py-2.5 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 shadow-lg">Execute Action</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityCentre;